import React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { Box, Button, Card, CardContent, Chip, IconButton, Stack, TextField, Typography } from '@mui/material';

import Image from '~/components/Common/Image';

import { FlatImageItem } from './types';

type SortableResult = ReturnType<typeof useSortable>;

interface Props {
  item: FlatImageItem;
  descriptionLabel: string;
  onDelete: (itemId: string | number) => void;
  onDescriptionChange: (itemId: string | number, value: string) => void;
  onSetHero?: (itemId: string | number) => void;
  onToggleHidden?: (itemId: string | number) => void;
  showDeleteAction: boolean;
  showDescriptionField: boolean;
  showHeroAction: boolean;
  showHiddenToggle: boolean;
  deleteActionLabel: string;
  restoreActionLabel: string;
}

interface TileHeaderProps {
  attributes: SortableResult['attributes'];
  deleteActionLabel: string;
  isDragging: boolean;
  item: FlatImageItem;
  listeners: SortableResult['listeners'];
  onDelete: (itemId: string | number) => void;
  onSetHero?: (itemId: string | number) => void;
  onToggleHidden?: (itemId: string | number) => void;
  restoreActionLabel: string;
  setActivatorNodeRef: (element: HTMLElement | null) => void;
  showDeleteAction: boolean;
  showHeroAction: boolean;
  showHiddenToggle: boolean;
}

function renderTileHeader({
  attributes,
  deleteActionLabel,
  isDragging,
  item,
  listeners,
  onDelete,
  onSetHero,
  onToggleHidden,
  restoreActionLabel,
  setActivatorNodeRef,
  showDeleteAction,
  showHeroAction,
  showHiddenToggle,
}: TileHeaderProps) {
  const showSetHeroAction = showHeroAction && onSetHero && !item.isHero;
  const showHiddenAction = showHiddenToggle && !!onToggleHidden;

  return (
    <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={1}>
      <Stack alignItems="center" direction="row" spacing={1} sx={{ minWidth: 0, flexWrap: 'wrap' }}>
        <Chip label={`#${item.order}`} size="small" />
        {item.isHero ? <Chip color="warning" icon={<StarOutlineIcon />} label="Hero" size="small" /> : null}
        {showDeleteAction ? (
          item.isPendingDelete ? (
            <Button
              aria-label={restoreActionLabel}
              color="primary"
              size="small"
              startIcon={<UndoOutlinedIcon fontSize="small" />}
              variant="text"
              onClick={() => onDelete(item.id)}
            >
              Undo
            </Button>
          ) : (
            <IconButton aria-label={deleteActionLabel} color="error" size="small" onClick={() => onDelete(item.id)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )
        ) : null}
        {showSetHeroAction ? (
          <Button disabled={item.isPendingDelete} size="small" variant="text" onClick={() => onSetHero(item.id)}>
            Make hero
          </Button>
        ) : null}
        {showHiddenAction ? (
          <Button
            disabled={item.isPendingDelete}
            size="small"
            startIcon={item.isHidden ? <VisibilityRoundedIcon /> : <VisibilityOffRoundedIcon />}
            variant="text"
            onClick={() => onToggleHidden(item.id)}
          >
            {item.isHidden ? 'Show image' : 'Hide image'}
          </Button>
        ) : null}
      </Stack>
      <IconButton
        {...attributes}
        {...listeners}
        ref={setActivatorNodeRef}
        aria-label="Reorder image"
        size="small"
        sx={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <DragIndicatorIcon />
      </IconButton>
    </Stack>
  );
}

export default function FlatImageTile({
  item,
  descriptionLabel,
  onDelete,
  onDescriptionChange,
  onSetHero,
  onToggleHidden,
  showDeleteAction,
  showDescriptionField,
  showHeroAction,
  showHiddenToggle,
  deleteActionLabel,
  restoreActionLabel,
}: Props) {
  const { attributes, isDragging, listeners, setActivatorNodeRef, setNodeRef, transform, transition } = useSortable({
    id: item.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const minTileHeight = showDescriptionField ? 470 : 390;

  const canPreviewWithPublicId = !!item.publicImageId;
  const preview = canPreviewWithPublicId ? (
    <Image
      alt={item.description || item.filename || item.displayId || 'image preview'}
      publicId={item.publicImageId}
      options={{ width: 640 }}
      style={{ display: 'block', height: '100%', objectFit: 'contain', width: '100%' }}
    />
  ) : (
    <Box
      component="img"
      alt={item.description || item.filename || item.displayId || 'image preview'}
      src={item.previewUrl}
      sx={{ display: 'block', height: '100%', objectFit: 'contain', width: '100%' }}
    />
  );

  return (
    <Card
      ref={setNodeRef}
      style={style}
      variant="outlined"
      sx={{
        minHeight: minTileHeight,
        opacity: item.isPendingDelete ? 0.55 : 1,
        borderColor: item.isHero ? 'warning.main' : 'divider',
        boxShadow: item.isHero ? 2 : 0,
      }}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
        {renderTileHeader({
          attributes,
          deleteActionLabel,
          isDragging,
          item,
          listeners,
          restoreActionLabel,
          setActivatorNodeRef,
          showDeleteAction,
          showHeroAction,
          showHiddenToggle,
          onDelete,
          onSetHero,
          onToggleHidden,
        })}

        <Box
          sx={{
            alignItems: 'center',
            aspectRatio: '16 / 9',
            display: 'flex',
            borderRadius: 1,
            justifyContent: 'center',
            overflow: 'hidden',
            bgcolor: 'grey.100',
            position: 'relative',
            '& img': item.isHidden
              ? {
                  filter: 'grayscale(1)',
                  opacity: 0.45,
                }
              : undefined,
          }}
        >
          {preview}
          {item.isHidden ? (
            <Chip
              label="Hidden image"
              size="small"
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                bgcolor: 'rgba(33, 33, 33, 0.78)',
                color: 'common.white',
                fontWeight: 600,
              }}
            />
          ) : null}
        </Box>

        <Stack spacing={0.5}>
          <Typography noWrap variant="subtitle2">
            {item.displayId || item.filename || item.id}
          </Typography>
          {item.filename && item.filename !== item.displayId ? (
            <Typography color="text.secondary" noWrap variant="body2">
              {item.filename}
            </Typography>
          ) : null}
        </Stack>

        {showDescriptionField ? (
          <TextField
            disabled={!item.descriptionEditable || item.isPendingDelete}
            fullWidth
            label={descriptionLabel}
            maxRows={3}
            minRows={1}
            multiline
            size="small"
            value={item.description || ''}
            onChange={(event) => onDescriptionChange(item.id, event.target.value)}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}
